import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "Do you help with UG, Masters, and MBA admissions?",
    a: "Yes. Mindways covers all three levels — Undergraduate, Masters, and MBA. Each track is handled with a process tailored to the distinct requirements of that level of study.",
  },
  {
    q: "When should we start if the student is in Grade 11 or their final year?",
    a: "For Grade 11 students, starting early is a significant advantage. It allows time for profile building, test preparation, and activity planning. For final-year students, starting immediately is still worthwhile — many intakes have longer planning windows than families assume.",
  },
  {
    q: "Do you provide psychometric testing?",
    a: "Where relevant, we incorporate structured evaluation tools including psychometric testing to help clarify course direction and career fit. This is part of the initial assessment phase.",
  },
  {
    q: "Do you help with IELTS, SAT, GRE, and GMAT planning?",
    a: "Yes. We advise on which tests are required for your target schools, set target scores, plan the test timeline, and integrate preparation into the overall application schedule. We work with test preparation partners where needed.",
  },
  {
    q: "Do you write SOPs for students?",
    a: "We guide the structure and narrative strategy of your Statement of Purpose. The student owns the final voice and story — we provide frameworks, feedback, and multiple review cycles. This produces more authentic, compelling SOPs.",
  },
  {
    q: "Do you charge for your services?",
    a: "Yes. We charge for our guidance and support. This keeps our recommendations aligned to your goals, with no conflicts or hidden incentives. Fees depend on your study level, timeline, and the scope you need.",
  },
  {
    q: "Can you help with scholarships?",
    a: "Yes. We map scholarship opportunities based on your profile and target schools, assess eligibility, and integrate scholarship applications into the overall planning calendar. We do not guarantee outcomes, but we identify and pursue every viable option.",
  },
  {
    q: "Which countries do you cover?",
    a: "Our core advisory covers the UK, USA, Germany, Ireland, and Dubai/UAE. We also advise for other major destinations — Canada, Australia, Netherlands, and others — based on each student's profile and goals.",
  },
  {
    q: "What is included in visa guidance?",
    a: "We provide a documentation checklist, financial documentation guidance, filing support, error prevention review, and interview preparation where required. Our 99% visa success rate reflects the rigour of our process.",
  },
  {
    q: "Do you help with accommodation and pre-departure?",
    a: "Yes. Pre-departure support includes accommodation guidance, budgeting, travel insurance, packing preparation, and connections to forex and ticketing partners where relevant.",
  },
  {
    q: "What happens in the first strategy call?",
    a: "The first call is a structured conversation covering the student's academic background, career goals, target countries and programmes, timeline, constraints, and family priorities. It ends with a clear picture of the next steps and a proposed roadmap.",
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      style={{ padding: "100px 0", backgroundColor: "#F8FAFC" }}
    >
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "0 24px" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: "center", marginBottom: "60px" }}
        >
          <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
            Questions Answered
          </span>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: "700", color: "#0F172A", margin: "12px 0 0" }}>
            Frequently asked questions
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                style={{ borderBottom: "1px solid #E2E8F0" }}
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#0F172A",
                    padding: "20px 0",
                    textAlign: "left",
                    lineHeight: "1.5",
                  }}
                >
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-answer-${i}`}
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "15px",
                    color: "#475569",
                    lineHeight: "1.7",
                    paddingBottom: "20px",
                  }}
                >
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
